import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FaShoppingBag,
  FaMobileAlt, 
  FaLaptopCode,
  FaServer,
  FaRobot, 
  FaCheckCircle, 
  FaArrowRight 
} from 'react-icons/fa';

const Services = ({ isHomePage = false }) => {
  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  // 1. Custom Shopify Stores & Headless E-Commerce
  const shopifyColumn1 = [
    'Bespoke Shopify 2.0 Theme Engineering (Liquid & Tailwind)',
    'Headless E-Commerce with Shopify Hydrogen & Storefront API',
    'Custom Recharge & Auto-Ship Subscription Portals',
    'High-Converting 1-Click AJAX Cart Drawers & Upsells',
    'Tiered Mix-and-Match Bundle Builders & Volume Discounts',
  ];
  const shopifyColumn2 = [
    'Wholesale B2B Gated Portals & Custom Pricing Tiers',
    'Speed Optimization (Sub-1s Page Speeds & 95+ Core Web Vitals)',
    'Klaviyo SMS/Email Marketing & Omnisend Integration',
    'Multi-Currency International Checkout (120+ Currencies)',
    'Seamless App Migrations & Custom Private Shopify Apps',
  ];

  // 2. Cross-Platform Mobile App Development
  const mobileColumn1 = [
    'Cross-Platform Flutter Mobile Apps (iOS & Android 60fps UI)',
    'High-Performance React Native Mobile Development',
    'Interactive Biometric Authentication & Digital Wallets',
    'Real-Time Live GPS Tracking, Maps & Dispatch Routing',
    'Apple HealthKit, Google Fit & IoT Device Bluetooth Sync',
  ];
  const mobileColumn2 = [
    'Offline-First Local Database Architecture (SQLite / Hive)',
    'Push Notifications (FCM, OneSignal) & In-App Messaging',
    'Native Camera Scanning, Computer Vision & OCR Capture',
    'In-App Subscriptions (RevenueCat, Apple StoreKit, Google Play)',
    'App Store & Google Play Publishing, CI/CD & Maintenance',
  ];

  // 3. Custom SaaS Platforms & MVP Engineering
  const saasColumn1 = [
    'Rapid 0-to-1 SaaS MVP Prototyping & Architecture',
    'Full-Stack Modern Web Apps (React 18, Next.js 15, TypeScript)',
    'Multi-Tenant Database Architecture & Data Isolation',
    'Stripe Billing, Usage-Based Subscriptions & Invoicing',
    'Granular Role-Based Access Control (RBAC) & OAuth 2.0',
  ];
  const saasColumn2 = [
    'Interactive Real-Time KPI Dashboards & Data Visualizers',
    'Automated Onboarding & Self-Serve Team Workspaces',
    'Scalable Microservices with Node.js, Express & FastAPI',
    'PostgreSQL, MySQL, Redis Caching & Prisma / TypeORM',
    'SOC2-Compliant Defensive Security & Audit Trail Logging',
  ];

  // 4. High-Performance Backend APIs & Cloud DevOps
  const backendColumn1 = [
    'High-Throughput REST & GraphQL API Engineering',
    'Low-Latency WebSockets for Real-Time Live Feeds',
    'Automated Docker Containerization & Kubernetes Clusters',
    'CI/CD Automated Deployment Pipelines (GitHub Actions)',
    'Database Sharding, Index Tuning & Connection Pooling',
  ];
  const backendColumn2 = [
    'AWS, Google Cloud & DigitalOcean Cloud Infrastructure',
    'Serverless Functions & Event-Driven Message Queues (RabbitMQ/Kafka)',
    'API Rate Limiting, DDoS Shielding & Cloudflare CDN',
    'Automated Daily Backups, Disaster Recovery & Failovers',
    '24/7 DevOps Monitoring, Alerting & Uptime SLA Guarantees',
  ];

  // 5. Secondary: AI Automations & Intelligent Agents Suite
  const aiSuiteColumn1 = [
    'Autonomous Multi-Agent AI Workflow Pipelines (n8n, LangChain, CrewAI)',
    '24/7 Conversational AI Voice Calling Agents (Retell, ElevenLabs, Vapi)',
    'Custom RAG Knowledge-Grounded Chatbots (Pinecone, OpenAI, WhatsApp)',
    'CRM, ERP & Automated Invoice Extraction & Data Sync',
  ];
  const aiSuiteColumn2 = [
    'Deep Web & Mobile LLM Integrations (Claude 3.5, GPT-4o, Gemini)',
    'AI-Powered Recommendation Engines & Semantic Search',
    'Computer Vision & Automated Image Processing Pipelines',
    'Local LLM Private Deployments (Ollama / vLLM on Private Cloud)',
  ];

  const renderServiceList = (services) => (
    <ul className="space-y-2.5">
      {services.map((service, index) => (
        <li key={index} className="flex items-start gap-2.5 text-gray-700 text-sm sm:text-base">
          <FaCheckCircle className="text-red-500 mt-1 shrink-0 text-base" />
          <span>{service}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="bg-gradient-to-b from-gray-50 via-white to-gray-50 py-12">
      {/* Header Banner */}
      <div className="w-[92%] md:w-[70%] max-w-5xl m-auto px-4 text-center mb-16">
        <span className="text-red-600 font-bold uppercase tracking-wider text-sm border-b-2 border-red-600 pb-1 inline-block mb-3">
          Core Spectators Engineering Services
        </span>
        {isHomePage ? (
          <h2 className="font-extrabold text-3xl sm:text-4xl lg:text-5xl text-gray-900 leading-tight">
            High-Converting <span className="text-red-600">Shopify Stores</span>, Mobile Apps & <span className="text-red-600">SaaS MVPs</span>
          </h2>
        ) : (
          <h1 className="font-extrabold text-3xl sm:text-4xl lg:text-5xl text-gray-900 leading-tight">
            High-Converting <span className="text-red-600">Shopify Stores</span>, Mobile Apps & <span className="text-red-600">SaaS MVPs</span>
          </h1>
        )}
        <p className="mt-4 text-gray-600 text-sm sm:text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
          We engineer high-performance custom Shopify 2.0 storefronts, cross-platform Flutter/React Native mobile applications, and zero-to-one SaaS platforms—accelerated by autonomous AI workflows and intelligent voice agents.
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 sm:space-y-16">
        
        {/* 1. Custom Shopify Stores & Headless E-Commerce */}
        <section className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition duration-300">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="lg:w-[45%] w-full bg-gradient-to-br from-red-50 to-gray-100 p-8 sm:p-12 flex flex-col justify-center items-center text-center">
              <div className="w-20 h-20 bg-red-600 text-white rounded-2xl flex items-center justify-center text-3xl shadow-lg mb-6">
                <FaShoppingBag />
              </div>
              <span className="bg-red-100 text-red-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                Primary Core Service
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Custom Shopify & <span className="text-red-600">Headless E-Commerce</span>
              </h2>
              <p className="mt-3 text-gray-600 text-sm leading-relaxed max-w-sm">
                Bespoke Shopify 2.0 themes, Recharge subscriptions, custom liquid checkout funnels, and sub-second headless Hydrogen storefronts built to maximize AOV and conversion rates.
              </p>
            </div>
            
            <div className="py-8 sm:py-10 lg:w-[55%] w-full px-6 sm:px-10 lg:px-12">
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <span className="w-2 h-6 bg-red-600 rounded-full inline-block"></span>
                Key Capabilities & Deliverables
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>{renderServiceList(shopifyColumn1)}</div>
                <div>{renderServiceList(shopifyColumn2)}</div>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-100 flex flex-wrap items-center justify-between gap-4">
                <span className="text-xs text-gray-500 font-medium">Stack: Shopify Liquid, Hydrogen, Recharge, Klaviyo, Tailwind, GraphQL</span>
                <Link 
                  onClick={handleScrollToTop} 
                  to="/Form" 
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2.5 rounded-lg shadow transition flex items-center gap-2 text-sm"
                >
                  Build Shopify Store <FaArrowRight className="text-xs" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Cross-Platform Mobile App Development */}
        <section className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition duration-300">
          <div className="flex flex-col lg:flex-row-reverse items-center">
            <div className="lg:w-[45%] w-full bg-gradient-to-br from-red-50 to-gray-100 p-8 sm:p-12 flex flex-col justify-center items-center text-center">
              <div className="w-20 h-20 bg-red-600 text-white rounded-2xl flex items-center justify-center text-3xl shadow-lg mb-6">
                <FaMobileAlt />
              </div>
              <span className="bg-red-100 text-red-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                Primary Core Service
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Cross-Platform <span className="text-red-600">Mobile Apps</span>
              </h2>
              <p className="mt-3 text-gray-600 text-sm leading-relaxed max-w-sm">
                Native-performance iOS & Android applications built with Flutter and React Native. Smooth 60fps micro-animations, real-time GPS tracking, offline databases, and in-app payments.
              </p>
            </div>
            
            <div className="py-8 sm:py-10 lg:w-[55%] w-full px-6 sm:px-10 lg:px-12">
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <span className="w-2 h-6 bg-red-600 rounded-full inline-block"></span>
                Key Capabilities & Deliverables
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>{renderServiceList(mobileColumn1)}</div>
                <div>{renderServiceList(mobileColumn2)}</div>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-100 flex flex-wrap items-center justify-between gap-4">
                <span className="text-xs text-gray-500 font-medium">Stack: Flutter, React Native, Dart, Swift, Kotlin, Firebase, SQLite</span>
                <Link 
                  onClick={handleScrollToTop} 
                  to="/Form" 
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2.5 rounded-lg shadow transition flex items-center gap-2 text-sm"
                >
                  Build Mobile App <FaArrowRight className="text-xs" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Custom SaaS Platforms & MVP Engineering */}
        <section className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition duration-300">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="lg:w-[45%] w-full bg-gradient-to-br from-red-50 to-gray-100 p-8 sm:p-12 flex flex-col justify-center items-center text-center">
              <div className="w-20 h-20 bg-red-600 text-white rounded-2xl flex items-center justify-center text-3xl shadow-lg mb-6">
                <FaLaptopCode />
              </div>
              <span className="bg-red-100 text-red-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                Primary Core Service
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Custom SaaS & <span className="text-red-600">MVP Engineering</span>
              </h2>
              <p className="mt-3 text-gray-600 text-sm leading-relaxed max-w-sm">
                Zero-to-one SaaS platform architecture for startups and enterprises. Multi-tenant security, Stripe subscription monetization, user team workspaces, and interactive analytics.
              </p>
            </div>
            
            <div className="py-8 sm:py-10 lg:w-[55%] w-full px-6 sm:px-10 lg:px-12">
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <span className="w-2 h-6 bg-red-600 rounded-full inline-block"></span>
                Key Capabilities & Deliverables
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>{renderServiceList(saasColumn1)}</div>
                <div>{renderServiceList(saasColumn2)}</div>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-100 flex flex-wrap items-center justify-between gap-4">
                <span className="text-xs text-gray-500 font-medium">Stack: React, Next.js 15, TypeScript, Node.js, PostgreSQL, Stripe</span>
                <Link 
                  onClick={handleScrollToTop} 
                  to="/Form" 
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2.5 rounded-lg shadow transition flex items-center gap-2 text-sm"
                >
                  Build SaaS Platform <FaArrowRight className="text-xs" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* 4. High-Performance Backend APIs & Cloud Infrastructure */}
        <section className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition duration-300">
          <div className="flex flex-col lg:flex-row-reverse items-center">
            <div className="lg:w-[45%] w-full bg-gradient-to-br from-red-50 to-gray-100 p-8 sm:p-12 flex flex-col justify-center items-center text-center">
              <div className="w-20 h-20 bg-red-600 text-white rounded-2xl flex items-center justify-center text-3xl shadow-lg mb-6">
                <FaServer />
              </div>
              <span className="bg-red-100 text-red-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                Primary Core Service
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Backend APIs & <span className="text-red-600">Cloud Architecture</span>
              </h2>
              <p className="mt-3 text-gray-600 text-sm leading-relaxed max-w-sm">
                Robust, scalable microservices, REST & GraphQL APIs, Redis caching layers, Docker containerization, and AWS/GCP cloud deployments built to handle millions of requests.
              </p>
            </div>
            
            <div className="py-8 sm:py-10 lg:w-[55%] w-full px-6 sm:px-10 lg:px-12">
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <span className="w-2 h-6 bg-red-600 rounded-full inline-block"></span>
                Key Capabilities & Deliverables
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>{renderServiceList(backendColumn1)}</div>
                <div>{renderServiceList(backendColumn2)}</div>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-100 flex flex-wrap items-center justify-between gap-4">
                <span className="text-xs text-gray-500 font-medium">Stack: Node.js, Express, FastAPI, PostgreSQL, Redis, Docker, AWS</span>
                <Link 
                  onClick={handleScrollToTop} 
                  to="/Form" 
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2.5 rounded-lg shadow transition flex items-center gap-2 text-sm"
                >
                  Architect Backend <FaArrowRight className="text-xs" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Secondary / Complementary AI Suite: AI Workflows, Voice Agents & Chatbots */}
        <section className="bg-white rounded-2xl shadow-xl border-2 border-gray-900 overflow-hidden hover:shadow-2xl transition duration-300">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="lg:w-[45%] w-full bg-gradient-to-br from-gray-900 to-black text-white p-8 sm:p-12 flex flex-col justify-center items-center text-center">
              <div className="w-20 h-20 bg-red-600 text-white rounded-2xl flex items-center justify-center text-3xl shadow-lg mb-6">
                <FaRobot />
              </div>
              <span className="bg-red-900/80 text-red-200 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3 border border-red-500/40">
                Smart Acceleration Suite
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">
                Intelligent AI Automations & <span className="text-red-500">Voice Agents</span>
              </h2>
              <p className="mt-3 text-gray-300 text-sm leading-relaxed max-w-sm">
                Supercharge your web & mobile apps with autonomous multi-agent workflows (n8n/LangChain), 24/7 AI voice calling agents, and custom RAG knowledge assistants.
              </p>
            </div>
            
            <div className="py-8 sm:py-10 lg:w-[55%] w-full px-6 sm:px-10 lg:px-12">
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <span className="w-2 h-6 bg-red-600 rounded-full inline-block"></span>
                AI Superpowers For Your Products
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>{renderServiceList(aiSuiteColumn1)}</div>
                <div>{renderServiceList(aiSuiteColumn2)}</div>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-100 flex flex-wrap items-center justify-between gap-4">
                <span className="text-xs text-gray-500 font-medium">AI Stack: Retell, ElevenLabs, Vapi, OpenAI, Claude 3.5, Pinecone, n8n</span>
                <Link 
                  onClick={handleScrollToTop} 
                  to="/Form" 
                  className="bg-gray-900 hover:bg-black text-white font-semibold px-6 py-2.5 rounded-lg shadow transition flex items-center gap-2 text-sm"
                >
                  Integrate AI Automations <FaArrowRight className="text-xs" />
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* Bottom CTA Card */}
      <div className="max-w-5xl mx-auto px-4 mt-20">
        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-3xl p-8 sm:p-12 text-white text-center shadow-2xl flex flex-col items-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-4">
            Ready to Build Your Shopify Store, App, or SaaS Platform?
          </h2>
          <p className="text-red-100 max-w-2xl text-sm sm:text-base mb-8">
            Tell us about your project goals. We analyze your requirements and deliver a customized scope breakdown, timeline estimate, and engineering roadmap.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link 
              onClick={handleScrollToTop} 
              to="/Form" 
              className="bg-white text-red-600 font-bold px-8 py-3.5 rounded-xl hover:bg-gray-100 shadow-lg transition text-base"
            >
              Get a Free Project Quote
            </Link>
            <Link 
              onClick={handleScrollToTop} 
              to="/Contact" 
              className="bg-black/30 hover:bg-black/50 text-white font-semibold px-8 py-3.5 rounded-xl border border-white/30 transition text-base"
            >
              Talk to Our Software Architects
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
